<template>
  <div data-view="packages">
    <router-view />

    <layout-logged-frame :title="title">
      <template v-slot:header-actions>
        <Can action="store" subject="package">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'packages-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_package') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:content>
        <PackagesFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :can-edit-columns="false"
          :items="packages"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectPackage"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope">
            <div v-if="scope.cell.column.value === 'labels'" class="package-labels">
              <span
                v-for="label in scope.cell.item.labels"
                :key="label.id"
                class="package-label"
              >
                <v-icon>label</v-icon>
                <span>{{ label.name }}</span>
              </span>
            </div>
            <div v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </div>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import PackagesFilters from '../../components/forms/filters/Package';
  import {extractNestedProp, isStr} from '../../utils';
  import columns from '../../config/packages/columns';

  export default {
    components: {
      PackagesFilters,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: '',
        },
        totalPages: 0,
        query: { per_page: 15 },
        isLoading: false,
      };
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.name === 'packages' &&
          to.query?.status === 'success' &&
          (from.name === 'packages-new' || from.name === 'packages-edit')
        ) {
          this.getPackages(this.query);
        }
      },
    },
    computed: {
      title() {
        const amount = this.totalCount > 0 ? `(${this.totalCount})` : '';
        return this.$t('packages') + ' ' + amount;
      },
      packages() {
        return this.$store.getters['packages/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      currentRoute() {
        return this.$route;
      },
    },
    methods: {
      getPackages(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('packages/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['packages/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectPackage(packageItem) {
        this.$store.dispatch('packages/setActiveItem', packageItem);
        this.$router.push({ name: 'packages-edit', params: { id: packageItem.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.getPackages(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getPackages(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.getPackages(this.query);
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };

        this.getPackages(this.query);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

[data-view='packages'] {
  .package-labels {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .package-label {
    display: flex;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }
}

[data-view='labels'] {
  .remove-label-box {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: map.get($colors, error);

    .v-icon {
      color: map.get($colors, error);
      margin: 0 4px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
